import * as Sentry from "@sentry/vue";
import { getCloudSession } from "./cloudAuth";
import {
  isJust,
  isNothing,
  just,
  matchMaybe,
  nothing,
  toMaybe,
  type Maybe,
} from "./maybe";

const HOST_ID = "sentry-feedback";
const DOCK_STYLE_ID = "zw-sentry-feedback-dock";

type FeedbackForm = {
  appendToDom: () => void;
  removeFromDom: () => void;
  open: () => void;
  close: () => void;
};

/** Single reused dialog. Recreating it re-appends Sentry CSS after our overrides. */
let form: Maybe<FeedbackForm> = nothing;
let open = false;
let closedListener: Maybe<() => void> = nothing;
/** True while we are closing programmatically (avoids double notify). */
let ignoreWidgetClose = false;

/** Called when the Sentry form is dismissed or submitted. Pass nothing to clear. */
export function onSentryFeedbackClosed(listener: Maybe<() => void>): void {
  closedListener = listener;
}

export function isSentryFeedbackOpen(): boolean {
  return open;
}

export async function openSentryFeedback(): Promise<boolean> {
  if (open) return true;

  const feedback = toMaybe(Sentry.getFeedback());
  if (isNothing(feedback)) return false;

  const session = toMaybe(getCloudSession());
  if (isJust(session) && session.value.email.length > 0) {
    Sentry.setUser({ email: session.value.email, id: session.value.userId });
  }

  if (isNothing(form)) {
    const raw: unknown = await feedback.value.createForm();
    const created =
      typeof raw === "object" && raw !== null
        ? just(raw as FeedbackForm)
        : nothing;
    if (isNothing(created)) return false;
    form = created;
    form.value.appendToDom();
  }

  // Always re-append so our rules stay after any Sentry <style> in the shadow root.
  applyDockChrome();
  open = true;
  form.value.open();
  // Sentry locks body scroll after open; writing UI should keep scrolling.
  requestAnimationFrame(() => {
    document.body.style.overflow = "";
  });
  return true;
}

export function closeSentryFeedback(): void {
  if (open === false || isNothing(form)) return;
  ignoreWidgetClose = true;
  try {
    form.value.close();
  } catch {
    /* already torn down */
  } finally {
    ignoreWidgetClose = false;
  }
  open = false;
  matchMaybe(closedListener, {
    just: (fn) => {
      fn();
    },
    nothing: () => {},
  });
}

/** Cancel / success from the widget. Keep the form mounted; just notify. */
export function handleSentryFeedbackFormClose(): void {
  if (ignoreWidgetClose) return;
  open = false;
  matchMaybe(closedListener, {
    just: (fn) => {
      fn();
    },
    nothing: () => {},
  });
}

const DOCK_CSS = `
  .dialog {
    background-color: transparent !important;
    align-items: flex-end !important;
    justify-content: flex-end !important;
    pointer-events: none !important;
    height: 100% !important;
    width: 100% !important;
  }

  .dialog__position {
    pointer-events: auto !important;
    inset: auto 0 0 auto !important;
    top: auto !important;
    right: 0 !important;
    bottom: var(--zw-status-dock-height, 2.25rem) !important;
    left: auto !important;
    padding: 0 !important;
    max-height: min(28rem, calc(100vh - var(--zw-status-dock-height, 2.25rem) - 1rem)) !important;
    align-items: flex-end !important;
    justify-content: flex-end !important;
  }

  /* Screenshot annotator needs room; leave the dock tuck for the form only. */
  .dialog__position:has(.editor) {
    inset: 1rem !important;
    padding: 0 !important;
    max-height: none !important;
    align-items: center !important;
    justify-content: center !important;
  }

  .dialog__content {
    width: 15.5rem !important;
    max-width: 15.5rem !important;
    gap: 0 !important;
    padding: 0 !important;
    overflow: hidden !important;
    border: 1px solid var(--line) !important;
    border-right: none !important;
    border-bottom: none !important;
    border-radius: 0.5rem 0 0 0 !important;
    box-shadow: none !important;
    background: var(--bg-raised) !important;
    color: var(--text) !important;
    font-family: var(--font-ui) !important;
    line-height: 1.35 !important;
  }

  .dialog__position:has(.editor) .dialog__content {
    width: min(96vw, 52rem) !important;
    max-width: min(96vw, 52rem) !important;
    border: 1px solid var(--line) !important;
    border-radius: 0.5rem !important;
  }

  .dialog__header {
    align-items: center !important;
    gap: 0.5rem !important;
    margin: 0 !important;
    padding: 0.75rem 0.85rem !important;
    border-bottom: 1px solid var(--line) !important;
    font-weight: 500 !important;
  }

  .dialog__title {
    width: auto !important;
    font-family: var(--font-heading) !important;
    font-size: 0.95rem !important;
    font-weight: 500 !important;
    color: var(--text-strong) !important;
  }

  .form {
    flex-direction: column !important;
    gap: 0 !important;
    padding: 0.85rem !important;
    overflow: auto !important;
  }

  .form__right {
    width: 100% !important;
    gap: 0.75rem !important;
  }

  .form__label {
    font-size: 0.8rem !important;
    color: var(--text-muted) !important;
  }

  .form__label__text {
    color: var(--text-muted) !important;
    font-weight: 500 !important;
  }

  .form__input {
    border: 1px solid var(--line) !important;
    border-radius: 0.35rem !important;
    color: var(--text) !important;
    font-weight: 400 !important;
    padding: 0.45rem 0.6rem !important;
    background: var(--bg) !important;
  }

  .form__input:focus-visible {
    outline: 2px solid var(--color-zw-amber) !important;
    outline-offset: 1px !important;
  }

  .btn-group {
    gap: 0.5rem !important;
    margin-top: 0.15rem !important;
  }

  .btn {
    border-radius: 0.35rem !important;
    font-family: var(--font-ui) !important;
    font-size: 0.82rem !important;
    font-weight: 500 !important;
    padding: 0.45rem 0.65rem !important;
    box-shadow: none !important;
  }

  .btn--primary {
    border-color: var(--color-zw-amber) !important;
    background: var(--color-zw-amber) !important;
    color: var(--bg-raised) !important;
  }

  .btn--default {
    background: transparent !important;
    color: var(--text-muted) !important;
  }

  .btn--default:hover {
    color: var(--text-strong) !important;
    background: var(--hover-fill) !important;
  }

  .success__position {
    inset: auto 0 0 auto !important;
    top: auto !important;
    right: 0 !important;
    bottom: var(--zw-status-dock-height, 2.25rem) !important;
    left: auto !important;
    padding: 0 !important;
  }

  .success__content {
    width: 15.5rem !important;
    border: 1px solid var(--line) !important;
    border-right: none !important;
    border-bottom: none !important;
    border-radius: 0.5rem 0 0 0 !important;
    box-shadow: none !important;
    background: var(--bg-raised) !important;
    color: var(--text) !important;
    font-family: var(--font-ui) !important;
  }
`;

/** Ensure dock overrides exist and are the last stylesheet in the shadow root. */
function applyDockChrome(): void {
  const host = toMaybe(document.getElementById(HOST_ID));
  if (isNothing(host)) return;
  const shadow = toMaybe(host.value.shadowRoot);
  if (isNothing(shadow)) return;

  const existingEl = shadow.value.getElementById(DOCK_STYLE_ID);
  const existing =
    existingEl instanceof HTMLStyleElement ? just(existingEl) : nothing;
  const style = matchMaybe(existing, {
    just: (el) => el,
    nothing: () => {
      const created = document.createElement("style");
      created.id = DOCK_STYLE_ID;
      created.textContent = DOCK_CSS;
      return created;
    },
  });
  // appendChild moves an existing node to the end (after any new Sentry styles).
  shadow.value.appendChild(style);
}
